import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Lock, BookOpen, Clock, CheckCircle } from 'lucide-react';
import axios from 'axios';
import SEO from '@/components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Chapter data with structure matching the book
const CHAPTERS = {
  free: [
    {
      slug: 'prologue',
      title: 'The Meeting',
      part: 'Introduction',
      number: 'PROLOGUE',
      readingTime: '8 min',
      firstLine: 'She had prepared for three weeks...',
    },
    {
      slug: 'chapter-1',
      title: 'What Fifty Years Found',
      part: 'Part One: Zero',
      number: 'CHAPTER ONE',
      readingTime: '14 min',
      firstLine: 'In 1972, a researcher sat down with a stack of studies...',
    },
    {
      slug: 'chapter-2',
      title: 'The Labyrinth',
      part: 'Part One: Zero',
      number: 'CHAPTER TWO',
      readingTime: '11 min',
      firstLine: 'When the ancient Greeks built their first labyrinths...',
    },
    {
      slug: 'chapter-3',
      title: 'What I Built',
      part: 'Part One: Zero',
      number: 'CHAPTER THREE',
      readingTime: '12 min',
      firstLine: 'I am a researcher...',
    },
  ],
  locked: [
    { slug: 'chapter-4', title: 'The Woman Who Reached the Stars', part: 'Part Two: They Built This' },
    { slug: 'chapter-5', title: 'The Door She Left Open', part: 'Part Two: They Built This' },
    { slug: 'chapter-6', title: 'At the Helm', part: 'Part Two: They Built This' },
    { slug: 'chapter-7', title: 'The Architecture of Ideas', part: 'Part Two: They Built This' },
    { slug: 'chapter-8', title: 'The Woman No One Knows', part: 'Part Two: They Built This' },
    { slug: 'chapter-9', title: 'Built from the Inside', part: 'Part Two: They Built This' },
    { slug: 'chapter-10', title: 'The Weight of Nations', part: 'Part Two: They Built This' },
    { slug: 'chapter-11', title: 'When Everything Is on Fire', part: 'Part Two: They Built This' },
    { slug: 'chapter-12', title: 'The Pledge', part: 'Part Three: What We Do Now' },
    { slug: 'chapter-13', title: 'The Labyrinth — The Film', part: 'Part Three: What We Do Now' },
    { slug: 'epilogue', title: 'Her Name Should Be Seen', part: 'Epilogue' },
  ],
};

const BookPage = () => {
  const downloadRef = useRef(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    country: '',
    role: '',
    source: '',
    newsletter: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const scrollToDownload = () => {
    downloadRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/book/request`, formData);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Error submitting request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="book-page" className="min-h-screen">
      <SEO 
        title="She Built This: The Book — by Dr. Neha Dewan"
        description="Read 'She Built This' by Dr. Neha Dewan. Fifty years of research. Zero proof that any program works. Five hundred and ninety-five women who built empires anyway. Free chapters available."
        keywords={['She Built This book', 'Dr. Neha Dewan', 'women leadership book', 'female founders book', 'gender equality research', 'women in business book']}
        url="https://shebuiltthis.com/book"
        image="https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=1200&q=80"
        type="book"
        author="Dr. Neha Dewan"
      />
      {/* ==================== HERO SECTION ==================== */}
      <section className="relative min-h-screen bg-sbt-black flex flex-col">
        {/* Gold top strip */}
        <div className="h-[6px] bg-sbt-gold" />
        
        <div className="flex-1 flex items-center justify-center px-6 py-20">
          <div className="text-center max-w-4xl mx-auto">
            {/* Eyebrow */}
            <p className="font-mono text-[13px] text-sbt-gold tracking-[0.3em] uppercase mb-8">
              SHE BUILT THIS  ·  A BOOK BY DR. NEHA DEWAN
            </p>
            
            {/* Title */}
            <h1 className="font-display text-5xl sm:text-7xl lg:text-[90px] font-bold text-sbt-white leading-[1.0] mb-8">
              She Built This
            </h1>
            
            {/* Subtitle */}
            <p className="font-body text-xl lg:text-[22px] text-sbt-dim italic mb-6 leading-relaxed">
              "Fifty Years. Zero Proof.
              <br />
              Five Hundred and Ninety-Five Women Who Built Anyway."
            </p>
            
            {/* Author */}
            <p className="font-subheading text-lg italic text-sbt-gold mb-4">
              Dr. Neha Dewan — Founder, She Built This
            </p>
            
            {/* Published line */}
            <p className="font-mono text-[13px] text-sbt-dim tracking-wide mb-12">
              shebuiltthis.co  ·  March 2026  ·  First Edition
            </p>
            
            {/* Gold ornamental rule */}
            <div className="flex items-center justify-center gap-4 mb-12">
              <div className="w-[100px] lg:w-[200px] h-[1px] bg-sbt-gold" />
              <div className="w-2 h-2 bg-sbt-gold rotate-45" />
              <div className="w-[100px] lg:w-[200px] h-[1px] bg-sbt-gold" />
            </div>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <Link 
                to="/book/prologue" 
                className="w-[200px] h-[52px] bg-sbt-green text-white font-mono text-[13px] tracking-[0.1em] uppercase flex items-center justify-center hover:bg-opacity-90 transition-all"
                data-testid="cta-start-reading"
              >
                Start Reading <ArrowRight className="ml-2" size={16} />
              </Link>
              <button 
                onClick={scrollToDownload}
                className="w-[200px] h-[52px] border border-sbt-gold text-sbt-gold font-mono text-[13px] tracking-[0.1em] uppercase flex items-center justify-center hover:bg-sbt-gold hover:text-sbt-black transition-all"
                data-testid="cta-get-book"
              >
                Get the Full Book
              </button>
            </div>
            
            {/* Small text */}
            <p className="font-mono text-[11px] text-sbt-dim">
              4 chapters free to read online  ·  Full book by request
            </p>
          </div>
        </div>
      </section>

      {/* ==================== BOOK SUMMARY SECTION ==================== */}
      <section className="bg-[#FAF8F2] section-padding">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-center">
            {/* Book Cover - Left */}
            <div className="lg:col-span-2">
              <div className="relative mx-auto max-w-[320px]">
                {/* Book shadow */}
                <div className="absolute inset-0 translate-x-4 translate-y-4 bg-sbt-black/20" />
                
                {/* Book cover */}
                <div className="relative bg-[#1F6B40] p-8 lg:p-10 border-4 border-sbt-gold">
                  <div className="text-center">
                    <h2 className="font-display text-3xl lg:text-[42px] font-bold text-white leading-tight mb-6">
                      SHE
                      <br />
                      BUILT
                      <br />
                      THIS
                    </h2>
                    
                    {/* Gold ornament */}
                    <div className="flex items-center justify-center gap-3 mb-6">
                      <div className="w-12 h-[1px] bg-sbt-gold" />
                      <div className="w-2 h-2 bg-sbt-gold rotate-45" />
                      <div className="w-12 h-[1px] bg-sbt-gold" />
                    </div>
                    
                    <p className="font-subheading text-lg italic text-sbt-gold mb-4">
                      Dr. Neha Dewan
                    </p>
                    
                    <p className="font-mono text-[11px] text-sbt-dim tracking-wider">
                      shebuiltthis.co
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Content - Right */}
            <div className="lg:col-span-3">
              <p className="font-mono text-[13px] text-sbt-green tracking-wider uppercase mb-4">
                ABOUT THE BOOK
              </p>
              
              <h3 className="font-display text-3xl lg:text-[36px] font-bold text-sbt-black leading-tight mb-6">
                "The room was not built for them."
              </h3>
              
              <div className="font-body text-[17px] text-[#3A4A40] leading-[1.8] space-y-4 mb-8">
                <p>
                  After fifty years of research — thousands of studies, billions spent on
                  mentoring programs, leadership workshops, diversity initiatives — the number
                  of programs that have ever been proven to produce more women at the top is:
                  <span className="font-bold text-sbt-black"> Zero.</span>
                </p>
                <p>
                  She Built This is a book about the women who built anyway.
                </p>
                <p>
                  Not in spite of the system. Not by waiting for the system to change.
                  By building a different one.
                </p>
                <p className="font-subheading italic text-sbt-black">
                  Thirteen chapters. One number. Five hundred and ninety-five women.
                  And a question that should not take fifty years to answer.
                </p>
              </div>
              
              {/* Stats row */}
              <div className="flex flex-wrap gap-6 mb-6 py-6 border-t border-b border-sbt-border">
                {[
                  { value: '13', label: 'Chapters' },
                  { value: '0', label: 'Programs Proven' },
                  { value: '595', label: 'Women Documented' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p className="font-display text-3xl font-bold text-sbt-green">{stat.value}</p>
                    <p className="font-mono text-xs text-sbt-dim tracking-wide">{stat.label}</p>
                  </div>
                ))}
              </div>
              
              <p className="font-mono text-[12px] text-sbt-dim">
                Estimated reading time: 4 hours 20 minutes
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== WHAT IS IN THIS BOOK ==================== */}
      <section className="bg-[#FAF8F2] section-padding border-t border-sbt-border">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="font-mono text-[13px] text-sbt-green tracking-wider uppercase mb-4">
              INSIDE THE BOOK
            </p>
            <h2 className="font-display text-3xl lg:text-[42px] font-bold text-sbt-black">
              Thirteen chapters. Three parts.
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Part One */}
            <div className="bg-sbt-green text-white p-8">
              <p className="font-mono text-xs tracking-wider mb-4 text-sbt-gold">PART ONE</p>
              <h3 className="font-display text-2xl font-bold mb-4">ZERO</h3>
              <p className="font-body text-sm leading-relaxed mb-6 text-white/90">
                The research. The finding. The number that stops every boardroom
                in the world. Three chapters that build the case for why the room
                needs to change — and why it has not.
              </p>
              <div className="font-mono text-xs text-white/70 space-y-1">
                <p>Chapter 1: What Fifty Years Found</p>
                <p>Chapter 2: The Labyrinth</p>
                <p>Chapter 3: What I Built</p>
              </div>
            </div>
            
            {/* Part Two */}
            <div className="bg-sbt-black text-[#FAF8F2] p-8">
              <p className="font-mono text-xs tracking-wider mb-4 text-sbt-gold">PART TWO</p>
              <h3 className="font-display text-2xl font-bold mb-4">THEY BUILT THIS</h3>
              <p className="font-body text-sm leading-relaxed mb-6 text-white/90">
                The women. Eight portraits of builders across industries and continents.
                Told without names — because their stories belong to all of us —
                but with every fact verified and every detail drawn from the record.
              </p>
              <div className="font-mono text-xs text-white/70 space-y-1">
                <p>Chapters 4 through 11</p>
              </div>
            </div>
            
            {/* Part Three */}
            <div className="bg-[#FAF8F2] text-sbt-black p-8 border-t-4 border-sbt-green">
              <p className="font-mono text-xs tracking-wider mb-4 text-sbt-green">PART THREE</p>
              <h3 className="font-display text-2xl font-bold mb-4">WHAT WE DO NOW</h3>
              <p className="font-body text-sm leading-relaxed mb-6 text-sbt-slate">
                The pledge. The film. The epilogue. What comes after reading —
                and what every person in every room can do differently
                starting this week.
              </p>
              <div className="font-mono text-xs text-sbt-dim space-y-1">
                <p>Chapter 12: The Pledge</p>
                <p>Chapter 13: The Labyrinth — The Film</p>
                <p>Epilogue: Her Name Should Be Seen</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== READ THE CHAPTERS ==================== */}
      <section className="bg-[#FAF8F2] section-padding border-t border-sbt-border">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="font-mono text-[13px] text-sbt-green tracking-wider uppercase mb-4">
              READ ONLINE — FREE
            </p>
            <h2 className="font-display text-3xl lg:text-[42px] font-bold text-sbt-black mb-4">
              Four chapters. Available now.
            </h2>
            <p className="font-body text-lg italic text-sbt-slate">
              No login. No paywall. Just read.
            </p>
          </div>
          
          {/* Free chapters grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {CHAPTERS.free.map((chapter) => (
              <Link 
                key={chapter.slug}
                to={`/book/${chapter.slug}`}
                className="bg-[rgba(255,255,255,0.02)] border border-[rgba(201,168,76,0.06)] p-6 hover:border-[rgba(201,168,76,0.2)] transition-all group border-l-4 border-l-[#2E8B57]"
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="font-mono text-xs text-sbt-dim tracking-wide">{chapter.part}</p>
                  <span className="pill-green text-[10px]">Available Now</span>
                </div>
                <h3 className="font-display text-xl lg:text-2xl font-bold text-sbt-black mb-2 group-hover:text-sbt-green transition-colors">
                  {chapter.number}: {chapter.title}
                </h3>
                <div className="flex items-center gap-4 mb-3">
                  <span className="font-mono text-xs text-sbt-dim flex items-center gap-1">
                    <Clock size={12} /> {chapter.readingTime}
                  </span>
                </div>
                <p className="font-body text-sm text-sbt-slate italic mb-4">
                  "{chapter.firstLine}"
                </p>
                <span className="font-mono text-xs text-sbt-green tracking-wide group-hover:underline">
                  Read {chapter.number === 'PROLOGUE' ? 'Prologue' : chapter.number} →
                </span>
              </Link>
            ))}
          </div>
          
          {/* Locked chapters list */}
          <div className="bg-[rgba(255,255,255,0.02)] border border-[rgba(201,168,76,0.06)] p-6 lg:p-8">
            <p className="font-mono text-xs text-sbt-dim tracking-wider uppercase mb-6">
              REMAINING CHAPTERS — GET THE FULL BOOK
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {CHAPTERS.locked.map((chapter) => (
                <button
                  key={chapter.slug}
                  onClick={scrollToDownload}
                  className="flex items-center justify-between p-4 bg-sbt-off-white hover:bg-sbt-gold-light transition-colors text-left group"
                >
                  <div>
                    <p className="font-mono text-[10px] text-sbt-dim mb-1">{chapter.part}</p>
                    <p className="font-subheading text-sm text-sbt-slate group-hover:text-sbt-black">
                      {chapter.title}
                    </p>
                  </div>
                  <Lock size={14} className="text-sbt-dim" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==================== SOCIAL PROOF STRIP ==================== */}
      <section className="bg-sbt-black py-6">
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          <p className="font-mono text-sm text-sbt-dim tracking-wide">
            2,400+ readers this month  ·  Read in 47 countries  ·  890 books requested
          </p>
        </div>
      </section>

      {/* ==================== GET THE FULL BOOK - FORM ==================== */}
      <section ref={downloadRef} className="bg-sbt-black section-padding-lg" id="get-book">
        <div className="border-t-2 border-sbt-gold" />
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 pt-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left - Info */}
            <div>
              <p className="font-mono text-[13px] text-sbt-gold tracking-wider uppercase mb-4">
                GET THE FULL BOOK
              </p>
              <h2 className="font-display text-4xl lg:text-[52px] font-bold text-white leading-tight mb-6">
                All thirteen chapters.
                <br />
                Yours to keep.
              </h2>
              <p className="font-body text-xl italic text-sbt-dim leading-relaxed mb-8">
                Free. No purchase required.
                <br />
                Request your copy and receive the complete PDF
                within 24 hours.
              </p>
              
              <div className="space-y-4">
                <p className="font-mono text-xs text-sbt-gold tracking-wider uppercase mb-4">
                  WHAT YOU RECEIVE
                </p>
                {[
                  'Complete 13-chapter book as a beautifully designed PDF',
                  'All verified research references',
                  'The nine labyrinth frameworks in full',
                  'The She Built This Builders\' Pledge',
                  'First access to future editions',
                  'Monthly She Built This reading notes from Dr. Neha Dewan',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="text-sbt-gold mt-1">✦</span>
                    <p className="font-body text-sbt-dim">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Right - Form */}
            <div>
              {submitted ? (
                <div className="bg-[#FAF8F2] p-8 text-center">
                  <CheckCircle size={48} className="text-sbt-green mx-auto mb-4" />
                  <h3 className="font-display text-2xl font-bold text-sbt-black mb-4">
                    Thank you for your request
                  </h3>
                  <p className="font-body text-sbt-slate">
                    Your copy of She Built This will arrive within 24 hours.
                    In the meantime — <Link to="/book/prologue" className="text-sbt-green underline">read the prologue</Link>.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-[#FAF8F2] p-6 lg:p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <input
                        type="text"
                        placeholder="Your first name"
                        required
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className="input-field"
                        data-testid="book-form-firstname"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        placeholder="Your last name"
                        required
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        className="input-field"
                        data-testid="book-form-lastname"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <input
                      type="email"
                      placeholder="Your email address"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-field"
                      data-testid="book-form-email"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder="Your country"
                      required
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      className="input-field"
                      data-testid="book-form-country"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder="Your role / what you do (optional)"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="input-field"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <select
                      value={formData.source}
                      onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                      className="select-field"
                      required
                    >
                      <option value="">How did you find She Built This?</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="Instagram">Instagram</option>
                      <option value="A friend">A friend</option>
                      <option value="Google">Google</option>
                      <option value="Event">Event</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  
                  <div className="mb-6">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.newsletter}
                        onChange={(e) => setFormData({ ...formData, newsletter: e.target.checked })}
                        className="mt-1 w-4 h-4 accent-sbt-green"
                        required
                      />
                      <span className="font-body text-sm text-sbt-slate">
                        I would like to receive the She Built This monthly reading notes
                        from Dr. Neha Dewan — stories, research and the women worth knowing about.
                      </span>
                    </label>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-[58px] bg-sbt-green text-white font-mono text-[13px] tracking-[0.1em] uppercase flex items-center justify-center hover:bg-opacity-90 transition-all disabled:opacity-50"
                    data-testid="book-form-submit"
                  >
                    {submitting ? 'Submitting...' : 'Request the Full Book'} <ArrowRight className="ml-2" size={16} />
                  </button>
                  
                  <p className="font-mono text-[11px] text-sbt-dim text-center mt-4">
                    Your details are never shared. Unsubscribe at any time.
                    <br />
                    You will receive your PDF within 24 hours.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ==================== ABOUT THE AUTHOR ==================== */}
      <section className="bg-sbt-black section-padding border-t border-sbt-gold/30">
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <p className="font-mono text-[13px] text-sbt-gold tracking-wider uppercase mb-4">
            ABOUT THE AUTHOR
          </p>
          <h2 className="font-display text-4xl lg:text-[52px] font-bold text-sbt-white mb-6">
            Dr. Neha Dewan
          </h2>
          <p className="font-body text-lg text-sbt-dim leading-relaxed mb-8 max-w-2xl mx-auto">
            Founder of She Built This. Co-Founder & CEO of SKI APAC FZCO. 
            Her research — a nine-framework systematic analysis of fifty years of evidence 
            on women in leadership — is currently under review at SAGE Open journal.
          </p>
          <Link to="/profiles/dr-neha-dewan" className="btn-primary">
            Read Her Profile <ArrowRight className="ml-2 inline" size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BookPage;
